
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import API from "../../services/api";
import {
  Card,
  DashboardShell,
  ProgressBar,
  SectionHeader,
  StatusBadge,
} from "../../components/dashboard/DashboardKit";
import {
  Home, FolderOpen, PlusCircle, ArrowLeft, ArrowRight,
  Zap, CheckCircle2, BarChart3, Hourglass, FolderX,
  Hash, Calendar, MapPin, Wrench,
} from "lucide-react";

const engineerNav = [
  { icon: <Home className="w-5 h-5" />, label: "Dashboard",   path: "/engineer"          },
  { icon: <FolderOpen className="w-5 h-5" />, label: "My Projects", path: "/engineer/projects"  },
  { icon: <PlusCircle className="w-5 h-5" />, label: "Add Project", path: "/engineer/add-project" },
];

const statusKey = (status = "") => {
  const v = String(status).toLowerCase();
  if (v.includes("complete") || v.includes("approved")) return "done";
  if (v.includes("delay")    || v.includes("reject"))   return "alert";
  if (v.includes("pending"))                             return "pending";
  return "active";
};

const toNum = (val) => Number(val) || 0;

// ── Status colour map ─────────────────────────────────────────────────────────
const STATUS_STYLES = {
  done:    { bg: "#ECFDF5", color: "#059669", border: "#A7F3D0", dot: "#059669" },
  alert:   { bg: "#FFF1F2", color: "#E11D48", border: "#FECDD3", dot: "#E11D48" },
  pending: { bg: "#FFFBEB", color: "#D97706", border: "#FDE68A", dot: "#D97706" },
  active:  { bg: "#EEF2FF", color: "#4F46E5", border: "#C7D2FE", dot: "#4F46E5" },
};

// ── Service-type accent colours (top card bar) ────────────────────────────────
const CARD_ACCENTS = [
  "linear-gradient(90deg,#5C2D91,#7B3FB5)",
  "linear-gradient(90deg,#4F46E5,#6366F1)",
  "linear-gradient(90deg,#0D9488,#14B8A6)",
  "linear-gradient(90deg,#D97706,#F59E0B)",
  "linear-gradient(90deg,#E11D48,#FB7185)",
  "linear-gradient(90deg,#059669,#34D399)",
];

const getAccent = (idx) => CARD_ACCENTS[idx % CARD_ACCENTS.length];

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

export default function EngineerProjects() {
  const navigate = useNavigate();
  const [filter,        setFilter]        = useState("All");
  const [segmentFilter, setSegmentFilter] = useState("All Segments");
  const [projects,      setProjects]      = useState([]);
  const [loading,       setLoading]       = useState(true);

  // ── HER ORIGINAL useEffect + API LOGIC — UNTOUCHED ───────────────────────
  useEffect(() => {
    API.get("/engineers/me/projects")
      .then((res) => {
        console.log("ENGINEER API RESPONSE =", res.data);

        if (res.data?.success) {
          const raw = res.data.projects || [];
          const valid = raw;

          console.log("VALID PROJECTS =", valid);
          console.log("[EngineerProjects] valid projects:", valid.length);
          setProjects(valid);
        }
      })
      .catch((err) => console.error("Engineer projects fetch failed:", err))
      .finally(() => setLoading(false));
  }, []);

  // ── STATUS FILTER PILLS ───────────────────────────────────────────────────
  const dynamicStatuses = Array.from(
    new Set(
      projects
        .map((p) => p.status || "Not Started")
        .filter((s) => s.toLowerCase() !== "archived")
    )
  );
  const statuses = [...dynamicStatuses, "Archived", "All"];

  // ── SEGMENT DROPDOWN OPTIONS ──────────────────────────────────────────────
  const segments = [
    "All Segments",
    ...Array.from(
      new Set(
        projects
          .map((p) => p.segment_name || p.segment)
          .filter((s) => s && String(s).trim() !== "")
      )
    ),
  ];

  // ── VISIBILITY LOGIC ──────────────────────────────────────────────────────
  const visible = projects.filter((p) => {
    const statusMatch =
      filter === "All"
        ? (p.status || "Not Started").toLowerCase() !== "archived"
        : filter === "Archived"
        ? (p.status || "Not Started").toLowerCase() === "archived"
        : (p.status || "Not Started") === filter;

    const segmentMatch =
  segmentFilter === "All Segments" ||
  String(p.segment_name || p.segment || "").trim() === segmentFilter;

    return statusMatch && segmentMatch;
  });

  const avgProgress = projects.length
    ? Math.round(
        projects.reduce((sum, p) => sum + toNum(p.progress), 0) / projects.length
      )
    : 0;

  // ── RENDER ────────────────────────────────────────────────────────────────
return (
    <>
    <style>{`@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800;900&display=swap');`}</style>
    <DashboardShell
      portal="Engineer Portal"
      roleLabel="ENGINEER"
      title="My Projects"
      subtitle="View and manage assigned live projects"
      navItems={engineerNav}
      stats={[
        { label: "Assigned",     value: projects.length,                                                                  className: "text-ap-violet" },
        { label: "Active",       value: projects.filter(p => String(p.status).toLowerCase().includes("active")).length,  className: "text-ap-orange" },
        { label: "Avg Progress", value: `${avgProgress}%`,                                                               className: "text-ap-red"    },
      ]}
      user={{
        initials: localStorage.getItem("name")?.slice(0, 2).toUpperCase() || "EN",
        name:     localStorage.getItem("name") || "Engineer",
        role:     "Engineer",
      }}
    >
<div style={{ fontFamily: "'Montserrat', sans-serif" }}>

        {/* ── BACK BUTTON ── */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
          <button
            onClick={() => navigate("/engineer")}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              background: "#fff",
              border: "1.5px solid #E2E8F0",
              borderRadius: 8,
              padding: "6px 14px",
              fontSize: 12,
              fontWeight: 600,
              color: "#5C2D91",
              cursor: "pointer",
              fontFamily: "'Montserrat', sans-serif",
              transition: "all 0.18s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "#5C2D91";
              e.currentTarget.style.background = "#EDE6F7";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "#E2E8F0";
              e.currentTarget.style.background = "#fff";
            }}
>
            <ArrowLeft size={14} /> Back to Dashboard
          </button>
        </div>

        {/* ── SUMMARY STAT STRIP ── */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
          gap: 14,
          marginBottom: 24,
        }}>
{[
            { icon: <FolderOpen size={20} />, label: "Total Assigned",  value: projects.length,                                                                 color: "#5C2D91", bg: "#EDE6F7", border: "#C9B5E5" },
            { icon: <Zap size={20} />,        label: "Active Projects",  value: projects.filter(p => String(p.status).toLowerCase().includes("active")).length,  color: "#4F46E5", bg: "#EEF2FF", border: "#C7D2FE" },
            { icon: <CheckCircle2 size={20} />, label: "Completed",        value: projects.filter(p => String(p.status).toLowerCase().includes("complet")).length, color: "#059669", bg: "#ECFDF5", border: "#A7F3D0" },
          ].map((s) => (
            <div key={s.label} style={{
              background: s.bg,
              border: `1.5px solid ${s.border}`,
              borderRadius: 16,
              padding: "16px 18px",
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}>
              <div style={{
                width: 40, height: 40, borderRadius: 12,
                background: "#fff",
                display: "flex", alignItems: "center", justifyContent: "center",
                color: s.color, flexShrink: 0,
                boxShadow: `0 2px 8px ${s.color}18`,
              }}>{s.icon}</div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: s.color, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 2 }}>{s.label}</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: s.color, lineHeight: 1 }}>{s.value}</div>
              </div>
            </div>
          ))}
        </div>

        {/* ── FILTER PANEL ── */}
        <div style={{
          background: "#fff",
          borderRadius: 18,
          border: "1.5px solid #E2E8F0",
          overflow: "hidden",
          boxShadow: "0 2px 12px rgba(92,45,145,0.06)",
          marginBottom: 6,
        }}>
          {/* Gradient colour bar */}
          <div style={{ height: 4, background: "linear-gradient(90deg,#5C2D91,#4F46E5,#0D9488)" }} />

          <div style={{
            padding: "18px 22px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 12,
            borderBottom: "1px solid #F1F5F9",
          }}>
            <div>
              <div style={{ fontWeight: 800, fontSize: 16, color: "#0F172A" }}>
                Assigned Projects
              </div>
              <div style={{ fontSize: 12, color: "#64748B", marginTop: 2 }}>
                {visible.length} live project{visible.length === 1 ? "" : "s"}
                {filter !== "All" && ` · filtered by "${filter}"`}
                {segmentFilter !== "All Segments" && ` · segment "${segmentFilter}"`}
              </div>
            </div>

            {/* Right: segment dropdown + status pills */}
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10 }}>

              {/* Segment dropdown */}
              {segments.length > 1 && (
                <select
                  value={segmentFilter}
                  onChange={(e) => setSegmentFilter(e.target.value)}
                  style={{
                    padding: "7px 12px",
                    borderRadius: 10,
                    border: segmentFilter !== "All Segments" ? "1.5px solid #5C2D91" : "1.5px solid #E2E8F0",
                    background: segmentFilter !== "All Segments" ? "#EDE6F7" : "#F8FAFC",
                    color: segmentFilter !== "All Segments" ? "#5C2D91" : "#64748B",
                    fontSize: 12,
                    fontWeight: 600,
                    fontFamily: "'Montserrat', sans-serif",
                    cursor: "pointer",
                    outline: "none",
                    appearance: "none",
                    WebkitAppearance: "none",
                    paddingRight: 28,
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%2364748B' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "right 10px center",
                  }}
                >
                  {segments.map((seg) => (
                    <option key={seg} value={seg}>{seg}</option>
                  ))}
                </select>
              )}

              {/* Status filter pills */}
              <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
                {statuses.map((s) => {
                  const isActive = filter === s;
                  const sk = statusKey(s);
                  const sc = s === "All"
                    ? { bg: "#5C2D91", border: "#5C2D91", color: "#fff", inactiveBg: "#EDE6F7", inactiveBorder: "#C9B5E5", inactiveColor: "#5C2D91" }
                    : s === "Archived"
                    ? { bg: "#6B5A8A", border: "#6B5A8A", color: "#fff", inactiveBg: "#F3F0F9", inactiveBorder: "#C4B8DC", inactiveColor: "#6B5A8A" }
                    : sk === "done"    ? { bg: "#059669", border: "#059669", color: "#fff", inactiveBg: "#ECFDF5", inactiveBorder: "#A7F3D0", inactiveColor: "#059669" }
                    : sk === "alert"   ? { bg: "#E11D48", border: "#E11D48", color: "#fff", inactiveBg: "#FFF1F2", inactiveBorder: "#FECDD3", inactiveColor: "#E11D48" }
                    : sk === "pending" ? { bg: "#D97706", border: "#D97706", color: "#fff", inactiveBg: "#FFFBEB", inactiveBorder: "#FDE68A", inactiveColor: "#D97706" }
                    :                    { bg: "#4F46E5", border: "#4F46E5", color: "#fff", inactiveBg: "#EEF2FF", inactiveBorder: "#C7D2FE", inactiveColor: "#4F46E5" };

                  return (
                    <button
                      key={s}
                      onClick={() => setFilter(s)}
                      style={{
                        padding: "6px 16px",
                        borderRadius: 20,
                        fontSize: 12,
                        fontWeight: 700,
                      fontFamily: "'Montserrat', sans-serif",
                        cursor: "pointer",
                        border: `1.5px solid ${isActive ? sc.border : sc.inactiveBorder}`,
                        background: isActive ? sc.bg : sc.inactiveBg,
                        color: isActive ? sc.color : sc.inactiveColor,
                        transition: "all 0.15s ease",
                        letterSpacing: "0.02em",
                      }}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* ── CARD GRID ── */}
          <div style={{ padding: "20px 22px" }}>

            {loading && (
              <div style={{ textAlign: "center", padding: "52px 0", color: "#94A3B8", fontSize: 14 }}>
                <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}><Hourglass size={36} /></div>
                Loading live projects...
              </div>
            )}

            {!loading && visible.length === 0 && (
              <div style={{ textAlign: "center", padding: "52px 0", color: "#94A3B8", fontSize: 14 }}>
                <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}><FolderX size={36} /></div>
                <div style={{ fontWeight: 700, fontSize: 15, color: "#0F172A", marginBottom: 6 }}>No projects found</div>
                <div>
                  {filter === "Archived"
                    ? "No archived projects found."
                    : "No assigned projects match the current filter."}
                </div>
              </div>
            )}

            {!loading && visible.length > 0 && (
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                gap: 18,
              }}>
                {visible.map((project, idx) => {
                  const progress = toNum(project.progress);
                  const sk       = statusKey(project.status);
                  const ss       = STATUS_STYLES[sk] || STATUS_STYLES.active;
                  const accent   = getAccent(idx);

                  return (
                    <ProjectCard
                      key={project.id}
                      project={project}
                      progress={progress}
                      ss={ss}
                      accent={accent}
                      onClick={() => {
                        // ── HER ORIGINAL NAVIGATION — UNTOUCHED ──
                        console.log(`[EngineerProjects] navigating to projects.id: ${project.navId}`);
                        navigate(`/engineer/project/${project.id}`);
                      }}
                    />
                  );
                })}
              </div>
            )}
          </div>
        </div>

      </div>
   </DashboardShell>
    </>
  );
}

// ── Project Card ──────────────────────────────────────────────────────────────
function ProjectCard({ project, progress, ss, accent, onClick }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: "#fff",
        borderRadius: 18,
        border: `1.5px solid ${hovered ? "#5C2D91" + "40" : "#E2E8F0"}`,
        overflow: "hidden",
        cursor: "pointer",
        transition: "all 0.18s ease",
        boxShadow: hovered ? "0 12px 32px rgba(92,45,145,0.14)" : "0 2px 8px rgba(0,0,0,0.04)",
        transform: hovered ? "translateY(-3px)" : "translateY(0)",
       fontFamily: "'Montserrat', sans-serif",
      }}
    >
      {/* Accent top bar */}
      <div style={{ height: 4, background: accent }} />

      <div style={{ padding: "18px 20px 20px" }}>

        {/* Header: project_id chip + status badge */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10, marginBottom: 10 }}>
          <div>
            {/* HER field: project.project_id */}
            <div style={{
              display: "inline-flex", alignItems: "center", gap: 5,
              background: "#EDE6F7", border: "1px solid #C9B5E5",
              borderRadius: 8, padding: "3px 10px",
              fontSize: 10, fontWeight: 800, color: "#5C2D91",
              letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 7,
            }}>
              <Hash size={11} /> {project.project_id}
            </div>

            <div style={{ fontWeight: 800, fontSize: 15, color: "#0F172A", lineHeight: 1.3, letterSpacing: "-0.01em" }}>
              {project.title || "Untitled Project"}
            </div>
          </div>

          {/* Status badge */}
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            background: ss.bg, border: `1px solid ${ss.border}`,
            borderRadius: 20, padding: "5px 11px",
            fontSize: 11, fontWeight: 700, color: ss.color,
            whiteSpace: "nowrap", flexShrink: 0,
          }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: ss.dot, display: "inline-block" }} />
            {project.status || "Not Started"}
          </div>
        </div>

        {/* Divider */}
        <div style={{ height: 1, background: "#F1F5F9", margin: "12px 0" }} />

        {/* Date row — HER fields: startDate / endDate */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
          {[
           { icon: <Calendar size={10} />, label: "Start", value: fmtDate(project.startDate || project.start_date) },
{ icon: <Calendar size={10} />, label: "End",   value: fmtDate(project.endDate || project.end_date)   },
          ].map((d) => (
            <div key={d.label} style={{
              background: "#F8FAFC", border: "1px solid #E2E8F0",
              borderRadius: 10, padding: "8px 10px",
            }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: "#94A3B8", marginBottom: 3, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                {d.icon} {d.label}
              </div>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#334155" }}>{d.value}</div>
            </div>
          ))}
        </div>

        {/* Location — HER field */}
        {project.location && (
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            background: "#F8FAFC", border: "1px solid #E2E8F0",
            borderRadius: 8, padding: "4px 10px",
            fontSize: 11, fontWeight: 600, color: "#64748B", marginBottom: 8,
          }}>
            <MapPin size={11} /> {project.location}
          </div>
        )}

        {/* Segment — HER logic: service_type guard, renders segment */}
        {project.service_type && (
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            background: "#F8FAFC", border: "1px solid #E2E8F0",
            borderRadius: 8, padding: "4px 10px",
            fontSize: 11, fontWeight: 600, color: "#64748B",
            marginBottom: 12, marginLeft: project.location ? 6 : 0,
          }}>
            <Wrench size={11} /> {project.segment_name || project.segment}
          </div>
        )}

        {/* Progress bar */}
        {progress > 0 && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
              <span style={{ fontSize: 10, fontWeight: 600, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.04em" }}>Progress</span>
              <span style={{ fontSize: 11, fontWeight: 800, color: "#5C2D91" }}>{progress}%</span>
            </div>
            <div style={{ height: 6, background: "#EDE6F7", borderRadius: 6, overflow: "hidden" }}>
              <div style={{
                height: "100%", width: `${progress}%`,
                background: "linear-gradient(90deg,#5C2D91,#7B3FB5)",
                borderRadius: 6, transition: "width 0.4s ease",
              }} />
            </div>
          </div>
        )}

        {/* CTA */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 6, paddingTop: 4 }}>
          <span style={{
            fontSize: 12, fontWeight: 700,
            color: hovered ? "#5C2D91" : "#94A3B8",
            transition: "color 0.15s",
            display: "flex", alignItems: "center", gap: 4,
          }}>
            View Details
            <span style={{
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              width: 22, height: 22, borderRadius: "50%",
              background: hovered ? "#5C2D91" : "#EDE6F7",
              color: hovered ? "#fff" : "#5C2D91",
              transition: "all 0.15s",
            }}>
              <ArrowRight size={12} />
            </span>
          </span>
        </div>

      </div>
    </div>
  );
}